import React, { useEffect, useImperativeHandle, forwardRef, useState } from "react";
import { useEditor, EditorContent, ReactRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import { api } from "../lib/api";

// ---------- Mention suggestion list ----------

const MentionList = forwardRef(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [items]);

  const selectItem = (index) => {
    const item = items[index];
    if (item) command({ id: item._id, label: item.username });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        setSelectedIndex((s) => (s + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelectedIndex((s) => (s + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) {
    return <div className="bg-white border border-ink-200 rounded-md shadow-md px-3 py-2 text-xs text-ink-400">No matches</div>;
  }

  return (
    <div className="bg-white border border-ink-200 rounded-md shadow-md py-1 max-h-64 overflow-y-auto min-w-[220px]">
      {items.map((u, i) => (
        <button
          key={u._id}
          onClick={() => selectItem(i)}
          className={[
            "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left",
            i === selectedIndex ? "bg-blue-50 text-blue-700" : "text-ink-700 hover:bg-ink-50",
          ].join(" ")}
        >
          <span className="w-6 h-6 rounded-full bg-ink-200 text-ink-600 flex items-center justify-center text-[10px] font-semibold">
            {(u.username || "?").charAt(0).toUpperCase()}
          </span>
          <span className="flex-1 truncate">{u.username}</span>
          <span className="text-[10px] text-ink-400 truncate max-w-[120px]">{u.email}</span>
        </button>
      ))}
    </div>
  );
});
MentionList.displayName = "MentionList";

const mentionSuggestion = {
  items: async ({ query }) => {
    try {
      const r = await api(`/users/lookup?q=${encodeURIComponent(query)}`);
      return (r.users || []).slice(0, 8);
    } catch { return []; }
  },
  render: () => {
    let component;
    let popup;
    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, { props, editor: props.editor });
        if (!props.clientRect) return;
        popup = tippy("body", {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },
      onUpdate: (props) => {
        component?.updateProps(props);
        if (!props.clientRect) return;
        popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
      },
      onKeyDown: (props) => {
        if (props.event.key === "Escape") { popup?.[0]?.hide(); return true; }
        return component?.ref?.onKeyDown(props);
      },
      onExit: () => { popup?.[0]?.destroy(); component?.destroy(); },
    };
  },
};

// ---------- Toolbar ----------

function ToolbarButton({ editor, action, isActive, label, kbd }) {
  if (!editor) return null;
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={action}
      title={kbd ? `${label} (${kbd})` : label}
      className={[
        "h-7 px-1.5 text-xs rounded font-medium transition",
        isActive ? "bg-blue-100 text-blue-700" : "text-ink-600 hover:bg-ink-100",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function Toolbar({ editor }) {
  if (!editor) return null;
  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-ink-100 bg-ink-50/60 rounded-t-md flex-wrap">
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")} label={<span className="font-bold">B</span>} kbd="Ctrl+B" />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")} label={<span className="italic">I</span>} kbd="Ctrl+I" />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive("strike")} label={<span className="line-through">S</span>} />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive("code")} label={<span className="font-mono">{`<>`}</span>} />
      <span className="w-px h-4 bg-ink-200 mx-1" />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })} label="H2" />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive("heading", { level: 3 })} label="H3" />
      <span className="w-px h-4 bg-ink-200 mx-1" />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")} label="•" />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")} label="1." />
      <ToolbarButton editor={editor} action={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive("blockquote")} label="❝" />
      <span className="w-px h-4 bg-ink-200 mx-1" />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const url = window.prompt("URL");
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        title="Link"
        className={`h-7 px-1.5 text-xs rounded font-medium transition ${editor.isActive("link") ? "bg-blue-100 text-blue-700" : "text-ink-600 hover:bg-ink-100"}`}
      >
        🔗
      </button>
      {editor.isActive("link") && (
        <button type="button" onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().unsetLink().run()}
          className="h-7 px-1.5 text-xs rounded font-medium text-ink-600 hover:bg-ink-100">unlink</button>
      )}
      <span className="ml-auto text-[10px] text-ink-400 px-2">@-mention · ** for bold · - for list</span>
    </div>
  );
}

// ---------- Editor ----------

const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, placeholder, readOnly = false, minHeight = 100, autoFocus = false, compact = false },
  ref
) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Link.configure({ openOnClick: !readOnly, autolink: true, HTMLAttributes: { class: "text-blue-600 underline", rel: "noopener noreferrer" } }),
      Placeholder.configure({ placeholder: placeholder || "Type something..." }),
      Mention.configure({
        HTMLAttributes: { class: "mention bg-blue-100 text-blue-700 px-1 rounded font-medium" },
        suggestion: { ...mentionSuggestion, char: "@" },
        renderLabel: ({ node }) => `@${node.attrs.label || node.attrs.id}`,
      }),
    ],
    content: value || "",
    editable: !readOnly,
    autofocus: autoFocus,
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: [
          "tiptap-content prose prose-sm max-w-none px-3 py-2 outline-none",
          readOnly ? "" : "min-h-[var(--mh)] cursor-text",
        ].join(" "),
        style: `--mh: ${minHeight}px`,
      },
    },
  });

  useImperativeHandle(ref, () => ({
    clear: () => editor?.commands.clearContent(),
    setHTML: (html) => editor?.commands.setContent(html || ""),
    getHTML: () => editor?.getHTML() || "",
    focus: () => editor?.commands.focus(),
  }));

  // Update content if prop value changes from outside (rare)
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line
  }, [editor, value]);

  if (readOnly) {
    return <EditorContent editor={editor} />;
  }
  return (
    <div className={`border border-ink-200 rounded-md bg-white focus-within:border-blue-300 ${compact ? "" : "shadow-sm"}`}>
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
});

export default RichTextEditor;
